import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { QHubBuddyLogo } from "@/components/QHubBranding";
import { Brain, Sparkles, Lightbulb, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PHASE_LABELS = {
  activacion: { label: "Activación", color: "bg-emerald-50 text-emerald-700" },
  central:    { label: "Diagnóstico", color: "bg-sky-50 text-sky-700" },
  adaptativo: { label: "Adaptativo", color: "bg-violet-50 text-violet-700" },
};

const DIMENSION_LABELS = {
  comprension: "Comprensión",
  inferencia: "Inferencia",
  analisis: "Análisis",
  resolucion: "Resolución",
  argumentacion: "Argumentación",
  transferencia: "Transferencia",
  persistencia: "Persistencia",
  metacognicion: "Metacognición",
};

const LEVEL_COLORS = {
  "Alto":          "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Medio":         "bg-amber-100 text-amber-700 border-amber-200",
  "En desarrollo": "bg-orange-100 text-orange-700 border-orange-200",
  "Por explorar":  "bg-gray-100 text-gray-500 border-gray-200",
};

const ResultView = ({ result, onContinue, onReset }) => (
  <div className="max-w-3xl mx-auto py-10 px-4 space-y-6" data-testid="reto-result">
    <div className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white rounded-3xl p-8 shadow-xl">
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="w-7 h-7" />
        <p className="uppercase tracking-wider text-sm opacity-90">Tu Perfil Cognitivo Inicial</p>
      </div>
      <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="profile-label">{result.profile_label}</h1>
      <p className="text-white/90 text-base leading-relaxed">{result.summary}</p>
    </div>

    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Tus dimensiones cognitivas</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(result.dimension_levels || {}).map(([k, lv]) => (
          <div key={k} className="flex items-center justify-between border border-gray-100 rounded-xl p-3" data-testid={`dim-row-${k}`}>
            <span className="text-sm text-gray-700">{DIMENSION_LABELS[k] || k}</span>
            <Badge className={`border ${LEVEL_COLORS[lv] || ""}`}>{lv}</Badge>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4">
        Tiempo total: {Math.round((result.persistence_seconds || 0) / 60)} min ·
        Los datos se usan para personalizar tu entrenamiento.
      </p>
    </div>

    <div className="flex gap-3 flex-wrap">
      <Button onClick={onContinue} className="flex-1 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="continue-to-tutor-btn">
        Continuar a Reto Avanzo <ArrowRight className="w-4 h-4" />
      </Button>
      <Button variant="outline" onClick={onReset} className="gap-2" data-testid="restart-diagnostic-btn">
        <RotateCcw className="w-4 h-4" /> Volver a intentar
      </Button>
    </div>
  </div>
);

const RetoAvanzoDiagnostic = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(16);
  const [index, setIndex] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const startTsRef = useRef(Date.now());
  const changeCountRef = useRef(0);

  // Boot: check status, resume or start
  useEffect(() => {
    if (!user) return;
    const boot = async () => {
      try {
        const st = await axios.get(`${API}/reto-avanzo/diagnostic/status`, { withCredentials: true });
        if (st.data?.completed) {
          setResult(st.data.result);
          setLoading(false);
          return;
        }
        const sr = await axios.post(`${API}/reto-avanzo/diagnostic/start`, {}, { withCredentials: true });
        if (sr.data?.completed) {
          setResult(sr.data.result);
        } else {
          setIndex(sr.data.current_index);
          setTotal(sr.data.total);
          setChallenge(sr.data.challenge);
          startTsRef.current = Date.now();
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "No se pudo iniciar el diagnóstico");
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [user]);

  const onAnswer = (e) => {
    setAnswer(e.target.value);
    changeCountRef.current += 1;
  };

  const submit = async () => {
    if (!challenge || submitting) return;
    if (!answer.trim()) { toast.error("Escribe tu respuesta antes de continuar."); return; }
    setSubmitting(true);
    try {
      const elapsed_ms = Date.now() - startTsRef.current;
      const res = await axios.post(`${API}/reto-avanzo/diagnostic/answer`, {
        index,
        answer,
        elapsed_ms,
        change_count: changeCountRef.current,
      }, { withCredentials: true });

      setFeedback(res.data?.feedback || "");
      if (res.data?.completed) {
        setResult(res.data.result);
        setChallenge(null);
      } else {
        setChallenge(res.data.challenge);
        setIndex(res.data.current_index + 1);
        setAnswer("");
        changeCountRef.current = 0;
        startTsRef.current = Date.now();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar respuesta");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("¿Volver a iniciar el diagnóstico? Se borrarán tus respuestas anteriores.")) return;
    try {
      await axios.post(`${API}/reto-avanzo/diagnostic/reset`, {}, { withCredentials: true });
      setResult(null); setIndex(0); setChallenge(null); setAnswer(""); setFeedback("");
      setLoading(true);
      const sr = await axios.post(`${API}/reto-avanzo/diagnostic/start`, {}, { withCredentials: true });
      setIndex(sr.data.current_index);
      setTotal(sr.data.total);
      setChallenge(sr.data.challenge);
      startTsRef.current = Date.now();
    } catch (e) {
      toast.error("No se pudo reiniciar");
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center" data-testid="diag-loading">
        <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-gray-100 rounded-2xl p-6 text-center space-y-3">
          <Brain className="w-10 h-10 text-sky-500 mx-auto" />
          <p className="text-gray-700">{error}</p>
          <Button onClick={() => navigate("/dashboard")} data-testid="diag-back-dashboard">Volver al panel</Button>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-mesh">
        <header className="bg-white/80 backdrop-blur border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <QHubBuddyLogo size="default" />
            <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="diag-header-back">Cerrar</Button>
          </div>
        </header>
        <ResultView result={result} onContinue={() => navigate("/tutor/reto-avanzo")} onReset={reset} />
      </div>
    );
  }

  if (!challenge) return null;
  const pct = Math.round(((index - 1) / total) * 100);
  const phaseMeta = PHASE_LABELS[challenge.phase] || { label: challenge.phase, color: "bg-gray-50 text-gray-700" };

  return (
    <div className="min-h-screen bg-mesh">
      <header className="bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <QHubBuddyLogo size="default" />
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>Reto {index} de {total}</span>
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-xs" data-testid="diag-header-close">Cerrar</Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <Progress value={pct} className="h-1" data-testid="diag-progress" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-2">
          <Badge className={phaseMeta.color}>{phaseMeta.label}</Badge>
          <Badge variant="outline" className="text-gray-500">Bloom · {challenge.bloom}</Badge>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="diag-prompt">
          <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed">{challenge.prompt}</pre>
        </div>

        {challenge.hint && (
          <div className="flex items-start gap-2 text-xs text-gray-500" data-testid="diag-hint">
            <Lightbulb className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> {challenge.hint}
          </div>
        )}

        <Textarea
          value={answer}
          onChange={onAnswer}
          placeholder="Escribe tu respuesta aquí..."
          rows={6}
          className="resize-none rounded-xl border-gray-200 focus:border-sky-500 focus:ring-sky-500"
          data-testid="diag-answer"
        />

        {feedback && (
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 text-sm text-sky-700" data-testid="diag-feedback">
            {feedback}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={submit} disabled={submitting || !answer.trim()} className="gap-2 bg-sky-600 hover:bg-sky-700" data-testid="diag-submit">
            {submitting ? "Enviando..." : (index >= total ? "Finalizar diagnóstico" : "Continuar")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default RetoAvanzoDiagnostic;
